import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CATEGORIES = ['All', 'VoIP', 'Calling Tips', 'Product Updates', 'Business', 'Security', 'Guides'];

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const fetchPosts = async (cat, pg, append = false) => {
        try {
            append ? setLoadingMore(true) : setLoading(true);
            const params = new URLSearchParams({ page: pg, limit: 9 });
            if (cat !== 'All') params.append('category', cat);

            const res = await fetch(`${API_URL}/blogs?${params}`);
            const data = await res.json();

            if (data.success) {
                setPosts(prev => append ? [...prev, ...data.data] : data.data);
                setHasMore(pg < data.pages);
            } else {
                setError('Failed to load posts.');
            }
        } catch (err) {
            setError('Could not connect to blog service.');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        setPage(1);
        fetchPosts(selectedCategory, 1);
    }, [selectedCategory]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPosts(selectedCategory, nextPage, true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div className="blog-page">
            <Helmet>
                <title>Blog | Ringez — VoIP & Calling Tips</title>
                <meta name="description" content="Read the latest articles on VoIP, international calling, business communication tips, and product updates from Ringez." />
            </Helmet>

            {/* Category Filters */}
            <div className="blog-filters">
                {CATEGORIES.map(cat => (
                    <button
                        key={cat}
                        className={`filter-pill ${selectedCategory === cat ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat)}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading ? (
                <div className="blog-loading">
                    <div className="blog-spinner" />
                    <p>Loading posts…</p>
                </div>
            ) : error ? (
                <div className="blog-error">
                    <p>{error}</p>
                </div>
            ) : posts.length === 0 ? (
                <div className="blog-empty">
                    <h3>No posts yet</h3>
                    <p>Check back soon for articles on {selectedCategory === 'All' ? 'VoIP and calling tips' : selectedCategory}.</p>
                </div>
            ) : (
                <>
                    <div className="blog-grid">
                        {posts.map(post => (
                            <Link key={post._id} to={`/blog/${post.slug}`} className="blog-card">
                                {post.featuredImage ? (
                                    <img
                                        src={post.featuredImage}
                                        alt={post.title}
                                        className="blog-card-image"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <div className="blog-card-image-placeholder">
                                        📝
                                    </div>
                                )}

                                <div className="blog-card-body">
                                    <div className="blog-card-meta">
                                        <span className="blog-category-badge">{post.category}</span>
                                        <span className="blog-date">
                                            {formatDate(post.publishedAt || post.createdAt)}
                                        </span>
                                    </div>
                                    <h2>{post.title}</h2>
                                    <p>{post.excerpt}</p>
                                </div>

                                <div className="blog-card-footer">
                                    <div className="blog-card-author">
                                        👤 {post.author} &nbsp;&bull;&nbsp; ⏱ {post.readTime || 3} min read
                                    </div>
                                    <span className="blog-card-arrow">→</span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {hasMore && (
                        <div className="blog-load-more">
                            <button
                                className="btn-load-more"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Loading…' : 'Load More Articles'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Blog;
